/**
 * Convert a date string in the format YYYY-MM or YYYY-MM-DD to a Date object.
 *
 * @param dateStr Date string in the format YYYY-MM or YYYY-MM-DD
 * @returns Date object
 */
export function getDateFromString(dateStr: string | undefined): Date | null {
  if (!dateStr) {
    return null;
  }

  const parts = dateStr.split("-");
  if (parts.length > 3) {
    return null;
  }
  const year = parseInt(parts[0], 10);
  if (isNaN(year)) {
    return null;
  }
  const month = parts.length > 1 ? parseInt(parts[1], 10) - 1 : 0; // Months are 0-indexed
  if (isNaN(month) || month < 0 || month > 11) {
    return null;
  }
  const day = parts.length > 2 ? parseInt(parts[2], 10) : 1; // Default to the first day of the month
  if (isNaN(day) || day < 1 || day > 31) {
    return null;
  }

  return new Date(year, month, day);
}

/**
 * Get a Date object representing the n-th Thursday of a given year and month.
 *
 * @param yearMonth Date in the format YYYY-MM
 * @param n The occurrence of Thursday to find (e.g., 3 for third Thursday)
 * @returns Date object
 */
export function getNthThursday(
  yearMonth: string | undefined,
  n: number,
): Date | null {
  let date = getDateFromString(yearMonth);
  if (!date) {
    return null;
  }

  let nthThursday: Date = date;
  let thursdayCount = 0;

  while (true) {
    if (date.getDay() === 4) {
      // 4 represents Thursday
      thursdayCount++;
      if (thursdayCount === n) {
        nthThursday = date;
        break;
      }
    }
    date.setDate(date.getDate() + 1);
  }

  return nthThursday;
}

/**
 * Get a Date object representing the second Thursday of a given year and month.
 *
 * @param yearMonth Date in the format YYYY-MM
 * @returns Date object
 */
export function getSecondThursday(yearMonth: string | undefined): Date | null {
  return getNthThursday(yearMonth, 2);
}

/**
 * Get a Date object representing the third Thursday of a given year and month.
 *
 * @param yearMonth Date in the format YYYY-MM
 * @returns Date object
 */
export function getThirdThursday(yearMonth: string | undefined): Date | null {
  return getNthThursday(yearMonth, 3);
}
